import moment from 'moment';
import type { Reminder } from '@/slices/remindersApi';
import {
	type Notifications,
	readNotifications,
	sendsNothing,
} from '../notifications/notificationSettings.ts';

// When the web app toasts a reminder. It follows the same rules as delivery in
// tasks (notificationSchedule.js, resolveRecipients.js), so a toast lands with
// the push or email it goes with. Pure so `node --test` can load it.

const MINUTE_MS = 60 * 1000;

/** A reminder with alerts that have arrived, one key per alert. */
export interface ReminderAlert {
	reminder: Reminder;
	keys: string[];
}

const isSnoozed = ({ snoozedUntil, dateTime }: Reminder) =>
	Boolean(snoozedUntil) && new Date(snoozedUntil as string).getTime() === new Date(dateTime).getTime();

// A snoozed reminder alerts at the snoozed time, not before it. None stays None.
const alertOffsets = (reminder: Reminder, { offsets }: Notifications) =>
	isSnoozed(reminder) && offsets.length > 0 ? [0] : offsets;

const isForUser = (reminder: Reminder, { toAssignees, toCreator }: Notifications, userId: string) => {
	const assigned = reminder._assignees?.length
		? reminder._assignees.map(({ _id }) => _id)
		: [reminder._assignee?._id ?? reminder._user];
	return (toAssignees && assigned.includes(userId)) || (toCreator && reminder._user === userId);
};

/** Every alert the user should have had by now, ignoring any already shown. */
export const arrivedAlerts = (
	reminders: Reminder[],
	{ userId, channel, now }: { userId: string; channel?: string; now: number },
): ReminderAlert[] => {
	if (channel === 'none') return [];

	return reminders.flatMap((reminder) => {
		const settings = readNotifications(reminder, 'reminder');
		if (reminder.isCompleted || sendsNothing(settings) || !isForUser(reminder, settings, userId)) {
			return [];
		}
		const dueMs = new Date(reminder.dateTime).getTime();
		const keys = alertOffsets(reminder, settings)
			.map((offset) => dueMs - offset * MINUTE_MS)
			.filter((sendAt) => sendAt <= now)
			.map((sendAt) => `${reminder._id}:${sendAt}`);
		return keys.length > 0 ? [{ reminder, keys }] : [];
	});
};

const isDue = ({ dateTime }: Reminder, now: number) => new Date(dateTime).getTime() <= now;

const timeLeft = ({ dateTime }: Reminder, now: number) => {
	const minutes = Math.ceil((new Date(dateTime).getTime() - now) / MINUTE_MS);
	if (minutes === 60) return '1 hour';
	return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

export const alertMessage = (alerts: ReminderAlert[], now: number) => {
	if (alerts.length === 1) {
		const { reminder } = alerts[0];
		return isDue(reminder, now)
			? `Reminder due: ${reminder.text}`
			: `Reminder in ${timeLeft(reminder, now)}: ${reminder.text}`;
	}
	return alerts.every(({ reminder }) => isDue(reminder, now))
		? `${alerts.length} reminders are now due`
		: `${alerts.length} reminders are due soon`;
};

// The list is oldest first and capped, so without a window a business with a
// lot of old open reminders would never reach the ones about to alert. It runs
// to tomorrow for alerts ahead of a reminder due just after midnight, and stays
// the same all day so the query only changes when the day does.
export const alertWindow = (now: number) => ({
	fromDate: moment(now).startOf('day').subtract(1, 'day').toISOString(),
	toDate: moment(now).add(1, 'day').endOf('day').toISOString(),
});
